import {ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, output} from '@angular/core';
import {SelectData} from "../../../../../types/records-table.types";
import {FieldsLineChartParams, LineChartDialogData, UpdateLineChartParams} from "../../types/line-chart-modal.types";
import {defaultRangeData, defaultRangeForm} from "../../configs/line-chart-filters-config";
import {LineChartFiltersService} from "../../services/line-chart-filters.service";
import {RangeData, RangeForm} from "../../types/line-chart-filters.types";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {customDateProvider} from "../../../../../locale/providers/custom-date.provider";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {
  MatDatepickerToggle,
  MatDatepickerToggleIcon,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from "@angular/material/datepicker";
import {DateInputDirective} from "../../../../../../shared/directives/date-input.directive";
import {MatIcon} from "@angular/material/icon";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatDivider} from "@angular/material/list";

@Component({
  selector: 'app-line-chart-filters',
  standalone: true,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatDateRangeInput,
    ReactiveFormsModule,
    FormsModule,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatEndDate,
    MatStartDate,
    DateInputDirective,
    MatSuffix,
    MatIcon,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatDivider
  ],
  templateUrl: './line-chart-filters.component.html',
  styleUrl: './line-chart-filters.component.scss',
  providers: [ ...customDateProvider ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineChartFiltersComponent implements OnInit, OnDestroy {
  lineChartDialogData = input.required<LineChartDialogData>();
  isArticlePage = input.required<boolean>();
  updateChart = output<UpdateLineChartParams>();

  private lineChartFiltersService: LineChartFiltersService = inject(LineChartFiltersService);

  public selectProductsData: Array<SelectData> = [];
  public columnsSelectData: Array<FieldsLineChartParams> = [];
  public selectArticleData: Array<SelectData> = [];
  public selectedProductId: string = '';
  public selectedArticleId: string = '';
  public selectedYear: number = 0;
  public selectedColumns: Array<string> = [];
  public rangeForm: FormGroup<RangeForm> = defaultRangeForm;
  private rangeData: RangeData = defaultRangeData;

  public ngOnInit(): void {
    this.initFiltersData();
  }

  public ngOnDestroy(): void {
    this.rangeForm.reset();
  }

  public isArticleReports(): boolean {
    return this.lineChartDialogData().parentUrl.includes('article');
  }

  public isAllSelected(): boolean {
    return this.selectedColumns.length === this.columnsSelectData.length;
  }

  public toggleSelectAll(): void {
    this.selectedColumns = this.isAllSelected() ?
      [] :
      this.columnsSelectData.map(option => option.key);
    this.handleUpdateChart();
  }

  public handleSelectChange(): void {
    this.handleUpdateChart();
  }

  public handleDateRangeChange(dateRangeStart: string | null, dateRangeEnd: string | null): void {
    this.rangeData = {
      dateFrom: dateRangeStart || '',
      dateTo: dateRangeEnd || ''
    };
    this.handleUpdateChart();
  }

  public clearDateRange(): void {
    this.rangeForm.reset();
    this.handleDateRangeChange(null, null);
  }

  private initFiltersData(): void {
    const { selectProductsData, columnsSelectData, selectArticleData, filters } = this.lineChartFiltersService.getInitialFiltersData(this.lineChartDialogData());
    this.selectProductsData = selectProductsData;
    this.columnsSelectData = columnsSelectData;
    this.selectArticleData = selectArticleData;
    this.selectedColumns = filters.selectedColumns;
    this.selectedYear = filters.selectedYear;
    this.selectedProductId = filters.selectedProductId;
    this.selectedArticleId = filters.selectedArticleId;

    const dateRange = this.lineChartFiltersService.getMinMaxDates(this.lineChartDialogData().queryParams);
    if (dateRange) {
      this.rangeForm.patchValue(dateRange);
      this.rangeData = this.lineChartFiltersService.formatDateRangeForDisplay(dateRange);
    }

    this.handleUpdateChart();
  }

  private handleUpdateChart(): void {
    this.updateChart.emit({
      selectedProductId: this.selectedProductId,
      selectedArticleId: this.selectedArticleId,
      selectedYear: this.selectedYear,
      selectedColumns: this.selectedColumns,
      columnsSelectData: this.columnsSelectData,
      rangeData: this.rangeData
    });
  }
}
